import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { articleCategories, articles, gossipArticles } from "@/mocks/articles";
import { hotTags } from "@/mocks/site";

const BASE = "https://danmeimh.net.cn";

export default function Gossip() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const category = params.get("category") ?? "";

  const list = useMemo(() => {
    let result = q ? articles : gossipArticles;
    if (category) {
      result = articles.filter((item) => item.category === category);
    }
    if (q) {
      const term = q.toLowerCase();
      result = result.filter((item) =>
        `${item.title}${item.excerpt}${item.tags.join("")}`.toLowerCase().includes(term),
      );
    }
    return result;
  }, [q, category]);

  const setCategory = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) {
      next.set("category", value);
    } else {
      next.delete("category");
    }
    setParams(next);
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "每日一瓜",
      url: `${BASE}/gossip`,
      description:
        "每日一瓜汇总每日精选娱乐行业话题，从剧集、综艺到音乐与平台生态，逐条讲清背后的机制。",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: list.slice(0, 10).map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${BASE}/article/${item.id}`,
          name: item.title,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "每日一瓜", item: `${BASE}/gossip` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="每日一瓜 | 黑料每日精选话题深度梳理"
        description="每日一瓜每天精选一个娱乐行业话题，从剧集换角、综艺剪辑到演唱会成本与平台生态，讲清楚背后的机制与逻辑。"
        keywords="黑料每日一瓜,每日一瓜,娱乐行业观察,剧集观察,综艺幕后"
        canonicalPath="/gossip"
        jsonLd={jsonLd}
      />
      <div className="px-4 md:px-8">
        <Breadcrumbs items={[{ label: "每日一瓜" }]} />
      </div>

      <section className="px-4 md:px-8 pb-6 border-b border-background-200">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[0.2em] text-accent-700 uppercase mb-2">
          <span className="w-6 h-px bg-accent-500" />
          DAILY STORY
        </span>
        <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-3">每日一瓜</h1>
        <p className="text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          每天挑一个值得聊的话题，把它背后的制作流程、商业逻辑与行业规则讲清楚。合集按分类整理，方便你快速找到想看的那一条。
        </p>
      </section>

      <section className="px-4 md:px-8 py-6" aria-label="分类筛选">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setCategory("")}
            className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
              category === ""
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            全部
          </button>
          {articleCategories.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setCategory(item)}
              className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                category === item
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {(q || category) && (
          <p className="mt-4 text-sm text-foreground-600">
            当前条件：
            {category && <span className="text-primary-600">分类「{category}」</span>}
            {q && <span className="text-primary-600 ml-2">关键词「{q}」</span>}
            <span className="ml-2">· 共 {list.length} 条</span>
            <Link to="/gossip" className="ml-3 text-foreground-500 hover:text-primary-600 transition-colors">
              清除筛选
            </Link>
          </p>
        )}
      </section>

      <section className="px-4 md:px-8 pb-4" aria-label="一瓜列表">
        {list.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {list.map((item) => (
              <ArticleCard key={item.id} article={item} variant="standard" />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-100 py-16 text-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400" aria-hidden="true" />
            <p className="mt-3 text-sm text-foreground-600">没有匹配的内容，换个关键词试试。</p>
            <Link
              to="/gossip"
              className="mt-4 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
            >
              返回全部分类
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>
        )}
      </section>

      <section className="px-4 md:px-8 py-10" aria-label="热门标签">
        <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">热门标签</h2>
        <div className="flex flex-wrap gap-2">
          {hotTags.map((tag) => (
            <Link
              key={tag}
              to={`/gossip?q=${encodeURIComponent(tag)}`}
              className="px-3 py-1.5 rounded-full border border-background-300 text-xs text-foreground-600 hover:border-primary-400 hover:text-primary-600 transition-colors"
            >
              #{tag}
            </Link>
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}